import { scaleTime } from 'd3-scale';
import { timeMinute, timeHour, timeDay, timeWeek, timeMonth, timeYear } from 'd3-time';

// Determine the granularity (e.g., minute, hour, day) based on the number of data points and interval
const getTimeSlice = (dataPointsNumber: number, interval: string) => {
  let timeSlice;

  switch (interval) {
    case '1m': {
      timeSlice = timeMinute;
      break;
    }
    case '1h': {
      if (dataPointsNumber <= 97) {
        timeSlice = timeHour;
      } else if (dataPointsNumber <= 169) {
        timeSlice = timeDay;
      }
      break;
    }
    case '1d': {
      if (dataPointsNumber >= 2 && dataPointsNumber < 71) {
        timeSlice = timeDay;
      } else if (dataPointsNumber >= 71 && dataPointsNumber < 731) {
        timeSlice = timeMonth;
      } else if (dataPointsNumber >= 731) {
        timeSlice = timeYear;
      }
      break;
    }
    case '1w': {
      if (dataPointsNumber >= 2 && dataPointsNumber < 9) {
        timeSlice = timeWeek;
      } else if (dataPointsNumber >= 9 && dataPointsNumber < 105) {
        timeSlice = timeMonth;
      } else if (dataPointsNumber >= 105) {
        timeSlice = timeYear;
      }
      break;
    }
    case '1M': {
      if (dataPointsNumber >= 2 && dataPointsNumber < 25) {
        timeSlice = timeMonth;
      } else if (dataPointsNumber >= 25) {
        timeSlice = timeYear;
      }
      break;
    }
    default: {
      timeSlice = timeDay;
      break;
    }
  }

  return timeSlice;
};

// Determine the step size between ticks based on the number of data points and interval
const getTickStep = (dataPointsNumber: number, interval: string) => {
  let tickStep = 1;

  switch (interval) {
    case '1m': {
      if (dataPointsNumber <= 6) {
        tickStep = 1;
      } else if (dataPointsNumber <= 15) {
        tickStep = 2;
      } else if (dataPointsNumber <= 61) {
        tickStep = 5;
      } else if (dataPointsNumber <= 120) {
        tickStep = 20;
      }
      break;
    }
    case '1h': {
      if (dataPointsNumber <= 13) {
        tickStep = 1;
      } else if (dataPointsNumber <= 25) {
        tickStep = 2;
      } else if (dataPointsNumber <= 97) {
        tickStep = 12;
      } else if (dataPointsNumber <= 169) {
        tickStep = 1;
      }
      break;
    }
    case '1d': {
      if (dataPointsNumber >= 2 && dataPointsNumber < 15) {
        tickStep = 1;
      } else if (dataPointsNumber >= 15 && dataPointsNumber < 29) {
        tickStep = 2;
      } else if (dataPointsNumber >= 29 && dataPointsNumber < 71) {
        tickStep = 7;
      } else if (dataPointsNumber >= 71) {
        tickStep = 1;
      }
      break;
    }
    case '1w': {
      if (dataPointsNumber >= 2 && dataPointsNumber < 9) {
        tickStep = 1;
      } else if (dataPointsNumber >= 9 && dataPointsNumber < 53) {
        tickStep = 1;
      } else if (dataPointsNumber >= 53 && dataPointsNumber < 105) {
        tickStep = 3;
      } else if (dataPointsNumber >= 105) {
        tickStep = 1;
      }
      break;
    }
    case '1M': {
      if (dataPointsNumber >= 2 && dataPointsNumber < 13) {
        tickStep = 1;
      } else if (dataPointsNumber >= 13 && dataPointsNumber < 25) {
        tickStep = 3;
      } else if (dataPointsNumber >= 25) {
        tickStep = 1;
      }
      break;
    }
    default: {
      tickStep = 1;
      break;
    }
  }

  return tickStep;
};

// Main function to calculate ticks based on data and interval
export const calculateXTicks = (data: { x: number }[], interval: string) => {
  if (!data || data.length === 0) return [];

  // Extract the min and max values of `x` from the data
  const min = Math.min(...data.map((item) => item.x));
  const max = Math.max(...data.map((item) => item.x));

  const scale = scaleTime().domain([new Date(min), new Date(max)]).range([0, 1]);
  const dataPointsNumber = data.length;

  const timeSlice = getTimeSlice(dataPointsNumber, interval);
  const tickStep = getTickStep(dataPointsNumber, interval);

  const ticks = scale.ticks(timeSlice, tickStep);
  return ticks.map((entry) => +entry); // Convert `Date` objects to timestamps
};
